<template>
  <div>
    <base-header
      class="pb-9 pt-4"
      :type="$route.meta.headerClass ? $route.meta.headerClass : 'success'"
    >
    </base-header>
    <modal :show.sync="showFormModal" size="lg" modal-classes="modal">
      <div class="p-4">
        <h4 class="mb-3">{{ editingId ? 'Edit input' : 'New input' }}</h4>
        <div class="row">
          <div class="col-md-8 mb-3">
            <base-input label="Name" v-model="form.name" placeholder="e.g. Edgbaston Feed" />
          </div>
          <div class="col-md-4 mb-3">
            <label class="form-control-label">Type</label>
            <el-select v-model="form.type" class="w-100">
              <el-option value="mediaconnect" label="MediaConnect flow" />
            </el-select>
          </div>
          <div class="col-12 mb-3">
            <base-input
              label="MediaConnect flow ARN"
              v-model="form.aws_flow_arn"
              placeholder="arn:aws:mediaconnect:eu-west-1:…"
            />
          </div>
          <div class="col-md-6 mb-3">
            <base-input
              label="Ingest protocol (display only)"
              v-model="form.ingest_protocol"
              placeholder="e.g. srt, rtmp"
            />
          </div>
          <div class="col-12 mb-3">
            <label class="form-control-label">Notes (optional)</label>
            <textarea v-model="form.notes" class="form-control" rows="2" />
          </div>
          <div class="col-12 mb-3 d-flex align-items-center">
            <base-switch v-model="form.enabled" class="mr-2" />
            <span class="small">Enabled</span>
          </div>
        </div>
        <div class="d-flex justify-content-end mt-2">
          <base-button type="secondary" class="mr-2" @click="showFormModal = false">
            Cancel
          </base-button>
          <base-button type="primary" :disabled="!form.name || saving" @click="save">
            Save
          </base-button>
        </div>
      </div>
    </modal>

    <div class="container-fluid mt--9">
      <div class="row">
        <div class="col">
          <div class="card shadow">
            <div class="card-header d-flex align-items-center justify-content-between flex-wrap">
              <span class="h3 mb-0">Inputs</span>
              <base-button type="primary" size="sm" @click="openAdd">
                <i class="fa fa-plus mr-1"></i>Add input
              </base-button>
            </div>
            <div class="card-body">
              <p class="text-muted small mb-3">
                Each input is a MediaConnect flow. Assign one to a match on the match's Live tab.
                At activation, MediaConnect outputs are automatically configured for the match's
                takers using their address and protocol.
              </p>
              <el-table
                class="table-card"
                :data="inputs"
                row-key="id"
                header-row-class-name="thead-light"
                :default-sort="{ prop: 'name', order: 'ascending' }"
              >
                <el-table-column label="Name" prop="name" min-width="180" sortable />
                <el-table-column label="Type" prop="type" width="140" sortable>
                  <template v-slot="{ row }">
                    <span>{{ typeLabel(row.type) }}</span>
                  </template>
                </el-table-column>
                <el-table-column label="ARN / Channel / Job" min-width="260" show-overflow-tooltip>
                  <template v-slot="{ row }">
                    <span class="small text-muted">{{ resourceId(row) }}</span>
                  </template>
                </el-table-column>
                <el-table-column label="Protocol" prop="ingest_protocol" width="100" />
                <el-table-column label="Enabled" width="90" align="center">
                  <template v-slot="{ row }">
                    {{ row.enabled !== false ? 'Yes' : 'No' }}
                  </template>
                </el-table-column>
                <el-table-column label="" width="160" align="right">
                  <template v-slot="{ row }">
                    <base-button size="sm" type="secondary" class="mr-1" @click="openEdit(row)">
                      Edit
                    </base-button>
                    <base-button size="sm" type="danger" @click="confirmDelete(row)">
                      Delete
                    </base-button>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'InputsAdmin',
  data() {
    return {
      showFormModal: false,
      editingId: null,
      saving: false,
      form: this.emptyForm(),
    }
  },
  computed: {
    inputs() {
      return this.$store.getters['inputs/array'] || []
    },
  },
  mounted() {
    this.$store.dispatch('inputs/fetchAndAdd')
  },
  methods: {
    emptyForm() {
      return {
        name: '',
        type: 'mediaconnect',
        aws_flow_arn: '',
        aws_medialive_channel_id: '',
        bc_live_id: '',
        ingest_protocol: '',
        notes: '',
        enabled: true,
      }
    },
    typeLabel(t) {
      if (t === 'mediaconnect') return 'MediaConnect'
      if (t === 'medialive') return 'MediaLive'
      if (t === 'brightcove') return 'Brightcove'
      return t || '—'
    },
    resourceId(row) {
      if (row.type === 'mediaconnect') return row.aws_flow_arn || '—'
      if (row.type === 'medialive') return row.aws_medialive_channel_id || '—'
      if (row.type === 'brightcove') return row.bc_live_id || '—'
      return '—'
    },
    openAdd() {
      this.editingId = null
      this.form = this.emptyForm()
      this.showFormModal = true
    },
    openEdit(row) {
      this.editingId = row.id
      this.form = {
        name: row.name || '',
        type: row.type || 'mediaconnect',
        aws_flow_arn: row.aws_flow_arn || '',
        aws_medialive_channel_id: row.aws_medialive_channel_id || '',
        bc_live_id: row.bc_live_id || '',
        ingest_protocol: row.ingest_protocol || '',
        notes: row.notes || '',
        enabled: row.enabled !== false,
      }
      this.showFormModal = true
    },
    async save() {
      if (!this.form.name || this.saving) return
      this.saving = true
      const payload = {
        name: this.form.name.trim(),
        type: this.form.type || 'mediaconnect',
        aws_flow_arn: this.form.aws_flow_arn.trim() || null,
        aws_medialive_channel_id: this.form.aws_medialive_channel_id.trim() || null,
        bc_live_id: this.form.bc_live_id.trim() || null,
        ingest_protocol: this.form.ingest_protocol.trim() || '',
        notes: this.form.notes.trim() || '',
        enabled: this.form.enabled !== false,
      }
      try {
        if (this.editingId) {
          await this.$store.dispatch('inputs/patch', { id: this.editingId, ...payload })
        } else {
          await this.$store.dispatch('inputs/insert', payload)
        }
        this.showFormModal = false
        this.$notify({ type: 'success', message: 'Saved', timeout: 2000 })
      } catch (e) {
        console.error(e)
        this.$notify({ type: 'danger', message: e.message || 'Save failed', timeout: 5000 })
      } finally {
        this.saving = false
      }
    },
    confirmDelete(row) {
      if (!window.confirm(`Delete input "${row.name || row.id}"?`)) return
      this.$store
        .dispatch('inputs/delete', row.id)
        .then(() => this.$notify({ type: 'success', message: 'Deleted', timeout: 2000 }))
        .catch((e) =>
          this.$notify({ type: 'danger', message: e.message || 'Delete failed', timeout: 5000 })
        )
    },
  },
}
</script>
